import { useState, forwardRef } from "react";
import ContentBox from "../../../actprep-ui/components/ContentBox";
import { apiUrls } from "../../../actprep-ui/utils/urls";
import PaginatedTable from "../../../actprep-ui/components/PaginatedTable";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import layout from "../../../actprep-ui/constants/layout";
import { useFilters, useDrawer } from "../../../actprep-ui/hooks/ui";
import { enrollmentProcessingFilters } from "../../../constants/filters";
import StudentsToProgramForm from "../../../components/StudentsToProgramForm/";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled, useTheme } from "@mui/material/styles";
import GenerateDataForm from "../../../components/GenerateDataForm";
import useDisclosure from "../../../actprep-ui/hooks/ui/useDisclosure";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { showSuccessSnackbar } from "../../../actprep-ui/utils/feedback";
import Edit from "@mui/icons-material/Edit";
import Link from "@mui/icons-material/Link";
import StudentProfileForm from "../../../components/StudentProfileForm";
import StudentImportDialog from "../../../components/StudentImportDialog/StudentImportDialog";
import Upload from "@mui/icons-material/Upload";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Close from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import palette from "../../../actprep-ui/constants/palette";
import dayjs from "dayjs";
import { apiOrigin } from "../../../actprep-ui/constants/api";
import {
    sexyBooleanColumn,
    sortingModes,
} from "../../../actprep-ui/constants/dataGrid";
import { useConfirm } from "material-ui-confirm";
import StudentEnrollmentInformation from "./StudentEnrollmentInformation";
import { tableNames } from "../../../constants/tables";
import { dataGridNestedColumnGetter } from "../../../actprep-ui/utils/tables";

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction='up' ref={ref} {...props} />;
});

function copyStudentOnboardingLink(studentId) {
    navigator.clipboard.writeText(
        `
        ${apiOrigin}/enrollment/student-signup/${studentId}/
    `.trim(),
    );

    showSuccessSnackbar("Student Onboarding Link Copied!");
}

const StyledDataGrid = styled(DataGridPro)(({ theme }) => {
    // Define colors with lower opacity for light and dark modes.
    const yellowBase =
        theme.palette.mode === "light"
            ? "rgba(255, 249, 196, 0.4)" // Light mode: 40% opacity yellow
            : "rgba(255, 249, 196, 0.25)"; // Dark mode: 25% opacity yellow
    const yellowHover =
        theme.palette.mode === "light"
            ? "rgba(255, 245, 157, 0.3)"
            : "rgba(255, 245, 157, 0.15)";
    const blueBase =
        theme.palette.mode === "light"
            ? "rgba(100, 255, 251, 0.4)" // Light mode: 40% opacity blue
            : "rgba(100, 255, 251, 0.25)"; // Dark mode: 25% opacity blue
    const blueHover =
        theme.palette.mode === "light"
            ? "rgba(100, 202, 249, 0.3)"
            : "rgba(100, 202, 249, 0.15)";

    return {
        "& .MuiDataGrid-columnHeader": {
            height: "unset !important",
        },
        "& .MuiDataGrid-columnHeaders": {
            maxHeight: "none !important",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: 1.2,
            textOverflow: "clip",
        },
        "& .super-app-theme--NEW_SIGNUP": {
            backgroundColor: yellowBase,
            "&:hover": {
                backgroundColor: yellowHover,
            },
            "&.Mui-selected": {
                backgroundColor: yellowBase,
                "&:hover": {
                    backgroundColor: yellowHover,
                },
            },
        },
        "& .super-app-theme--NEW_EXISTING_SIGNUP": {
            backgroundColor: blueBase,
            "&:hover": {
                backgroundColor: blueHover,
            },
            "&.Mui-selected": {
                backgroundColor: blueBase,
                "&:hover": {
                    backgroundColor: blueHover,
                },
            },
        },
    };
});

export default function EnrollmentProcessing() {
    const confirm = useConfirm();
    const theme = useTheme();
    const apiRef = useGridApiRef();
    const isPersistentFiltersDrawer = useMediaQuery((theme) =>
        theme.breakpoints.up("md"),
    );
    const sidebarWidth = 20;
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [editingStudent, setEditingStudent] = useState({
        rowId: null,
        student: null,
    });
    const [studentsToProgramAction, setStudentsToProgramAction] =
        useState("add");
    const [reloadTable, setReloadTable] = useState(false);
    const [studentsToProgramDialogTitle, setStudentsToProgramDialogTitle] =
        useState("");
    const handleAddStudentsToProgramClick = (event, selectedRows) => {
        setSelectedStudents(selectedRows);
        onStudentsToProgramDialogOpen();
        setStudentsToProgramAction("add");
        setStudentsToProgramDialogTitle("Add students to a Program.");
    };
    const handleRemoveStudentsFromProgramClick = (event, selectedRows) => {
        setSelectedStudents(selectedRows);
        onStudentsToProgramDialogOpen();
        setStudentsToProgramAction("remove");
        setStudentsToProgramDialogTitle("Remove students from a Program.");
    };

    const handleStudentToProgramActionComplete = () => {
        setReloadTable(true);
        onStudentsToProgramDialogClose();
    };

    const handleGenerateData = (event, selectedRows) => {
        setSelectedStudents(selectedRows);
        onGenerateDataDialogOpen();
    };

    const tableActions = [
        {
            label: "Add to Program",
            onClick: handleAddStudentsToProgramClick,
        },
        {
            label: "Remove from Program",
            onClick: handleRemoveStudentsFromProgramClick,
        },
        {
            label: "Generate Weeks",
            onClick: handleGenerateData,
        },
    ];
    const { open, setOpen: setFiltersDrawerOpen, Drawer } = useDrawer();
    const { Filters, FiltersAppliedList, getFilterParams } = useFilters({
        name: "Enrollment Processing",
        saveState: true,
        filtersConfig: enrollmentProcessingFilters,
        handleClose: () => setFiltersDrawerOpen(false),
    });
    const {
        onOpen: onStudentsToProgramDialogOpen,
        onClose: onStudentsToProgramDialogClose,
        getDisclosureProps: getStudentToProgramDialogProps,
    } = useDisclosure();

    const {
        onOpen: onGenerateDataDialogOpen,
        onClose,
        getDisclosureProps,
    } = useDisclosure();

    const {
        onOpen: onOpenEditStudentDialog,
        getDisclosureProps: getEditStudentDialogProps,
    } = useDisclosure();

    const {
        onOpen: onOpenImportDialog,
        onClose: onCloseImportDialog,
        getDisclosureProps: getImportDialogProps,
    } = useDisclosure();

    const handleEditStudent = (student, rowId) => {
        setEditingStudent({
            student: student,
            rowId: rowId,
        });
        onOpenEditStudentDialog();
    };

    const handleStudentProfileFormSubmit = (newStudentProfile) => {
        setReloadTable(true);
        setEditingStudent({
            rowId: editingStudent.rowId,
            student: {
                ...editingStudent.student,
                ...newStudentProfile,
            },
        });
    };

    const enrollmentProcessingColumns = [
        {
            field: "first_name",
            headerName: "First Name",
            width: 100,
        },
        {
            field: "last_name",
            headerName: "Last Name",
            width: 100,
        },
        {
            field: "email",
            headerName: "Email",
            width: 150,
        },
        {
            field: "phone",
            headerName: "Phone",
            width: 100,
        },
        {
            field: "t_shirt_size",
            headerName: "Shirt Size",
            width: 75,
        },
        {
            field: "shirt_material",
            headerName: "Shirt Material",
            width: 100,
        },
        {
            field: "enrollment_step",
            headerName: "Enrollment Step",
            width: 150,
        },
        {
            field: "school.name",
            headerName: "School",
            width: 100,
            valueGetter: dataGridNestedColumnGetter,
        },
        {
            field: "current_team",
            headerName: "Team",
            width: 100,
            valueGetter: dataGridNestedColumnGetter,
        },
        {
            field: "cohort.event_name",
            headerName: "Cohort",
            width: 100,
            valueGetter: dataGridNestedColumnGetter,
        },
        {
            field: "most_recent_ticket_program_type",
            headerName: "Program Type",
            width: 100,
        },
        {
            field: "most_recent_ticket_seat_type",
            headerName: "Seat Type",
            width: 100,
        },
        {
            field: "ordered_programs",
            headerName: "Assigned Programs",
            width: 400,
            valueGetter: (orderPrograms) =>
                orderPrograms
                    .map(
                        (orderedProgram) =>
                            `${orderedProgram.program.title} (${orderedProgram.start_date})`,
                    )
                    .join(", "),
        },
        {
            field: "start_scores_image_uploaded",
            headerName: "Scores Proof Uploaded",
            type: "boolean",
            width: 100,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "wall_of_greats_image_uploaded",
            headerName: "WOG Photo Uploaded",
            type: "boolean",
            width: 100,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "profile_pic_uploaded",
            headerName: "Profile Pic Uploaded",
            type: "boolean",
            width: 100,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "whatsapp_group_created",
            headerName: "Whatsapp Group Created",
            type: "boolean",
            width: 100,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "added_to_group_chat",
            headerName: "Added to chat",
            type: "boolean",
            width: 100,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "parent_chat_created",
            headerName: "Parent Chat Created",
            type: "boolean",
            width: 75,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "coaching_chat_created",
            headerName: "Coaching Chat Created",
            type: "boolean",
            width: 75,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "attending_in_person",
            headerName: "Attending In-Person",
            type: "boolean",
            width: 100,
            renderCell: ({ value, row }) => {
                value = row.most_recent_ticket_seat_type
                    ?.toLowerCase()
                    .includes("online")
                    ? null
                    : value;
                return sexyBooleanColumn({ value: value });
            },
        },
        {
            field: "supplies_shipped",
            headerName: "Supplies Shipped",
            type: "boolean",
            width: 100,
            renderCell: ({ value, row }) => {
                if (
                    row.most_recent_ticket_seat_type
                        ?.toLowerCase()
                        .includes("online") ||
                    !row.attending_in_person
                ) {
                    return sexyBooleanColumn({ value: value });
                }
                return null;
            },
        },
        {
            field: "memorization_text_sent",
            headerName: "Memorization Text Sent",
            type: "boolean",
            width: 100,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "data_created",
            headerName: "Weeks Generated",
            type: "boolean",
            width: 100,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "data_created_at",
            headerName: "Weeks Generated Date",
            type: "dateTime",
            width: 250,
            valueGetter: (val) => (val ? new Date(val) : null),
        },
        {
            field: "is_processed",
            headerName: "Processed",
            type: "boolean",
            width: 100,
            renderCell: sexyBooleanColumn,
        },
        {
            field: "is_processed_dt",
            headerName: "Processed Date",
            type: "dateTime",
            width: 250,
            valueGetter: (val) => (val ? new Date(val) : null),
            valueFormatter: (val) => val?.toLocaleDateString(),
            renderCell: (params) => {
                let backgroundColor = "none";
                const signupDate = params.row.most_recent_ticket_signup_date;
                const compDate = params.row.is_processed
                    ? dayjs(params.row.is_processed_dt)
                    : dayjs();
                const dayDiff = compDate.diff(dayjs(signupDate), "days");
                if (dayDiff >= 14) {
                    backgroundColor = palette.redRGBA(25);
                } else if (dayDiff >= 10) {
                    backgroundColor = palette.yellowRGBA(25);
                }

                return (
                    <Box
                        sx={{
                            backgroundColor: backgroundColor,
                            paddingLeft: "1rem",
                        }}
                    >
                        {params.value?.toLocaleDateString() || "N/A"}
                    </Box>
                );
            },
        },
        {
            field: "most_recent_ticket_signup_date",
            headerName: "Signup Date",
            type: "dateTime",
            width: 250,
            valueGetter: (val) => {
                return val ? new Date(val) : null;
            },
            valueFormatter: (val) => val?.toLocaleDateString(),
        },
        {
            headerName: "Actions",
            field: "actions",
            type: "actions",
            width: 75,
            getActions: ({ row, id }) => {
                return [
                    <Tooltip title='Edit Student' key={1}>
                        <GridActionsCellItem
                            icon={<Edit color='primary' />}
                            label='Edit Student'
                            onClick={() => handleEditStudent(row, id)}
                            sx={{ p: 0, m: 0, fontSize: "small" }}
                        />
                    </Tooltip>,
                    <Tooltip title='Copy Student Onboarding Link' key={3}>
                        <GridActionsCellItem
                            icon={<Link color='primary' />}
                            label='Copy Student Onboarding Link'
                            sx={{ p: 0, m: 0, fontSize: "small" }}
                            onClick={() => {
                                if (
                                    !row.email ||
                                    row.email.toLowerCase() ==
                                        row.parent.email.toLowerCase()
                                ) {
                                    confirm({
                                        title: "Cannot Onboard Student",
                                        description:
                                            "Before onboarding the student, you must change their email to be different than their parent's email",
                                    });
                                } else {
                                    copyStudentOnboardingLink(row.uuid);
                                }
                            }}
                        />
                    </Tooltip>,
                ];
            },
        },
    ];

    return (
        <>
            <Grid
                size={12}
                sx={{
                    marginTop: `${layout.navBarHeight + 1}rem`,
                }}
            >
                <ContentBox
                    style={{
                        maxHeight: "90vh",
                        height: "500rem",
                        marginRight:
                            open && isPersistentFiltersDrawer
                                ? `${sidebarWidth}rem`
                                : null,
                        transition: theme.transitions.create(
                            ["margin", "width"],
                            {
                                easing: theme.transitions.easing.easeOut,
                                duration:
                                    theme.transitions.duration.enteringScreen,
                            },
                        ),
                    }}
                >
                    <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                    >
                        <Typography variant='h5' fontWeight={"bold"}>
                            Enrollment Processing
                        </Typography>
                        <Button
                            variant='contained'
                            startIcon={<Upload />}
                            onClick={onOpenImportDialog}
                        >
                            Import Students
                        </Button>
                    </Stack>
                    <Box
                        sx={{
                            height: "95%",
                        }}
                    >
                        <PaginatedTable
                            DataGrid={StyledDataGrid}
                            columnHeaderHeight={80}
                            searchable
                            apiRef={apiRef}
                            actions={tableActions}
                            tableName={tableNames.ENROLLMENT_PROCESSING}
                            apiUrl={apiUrls.admin.enrollmentProcessing()}
                            columns={enrollmentProcessingColumns}
                            sortingMode={sortingModes.SERVER}
                            urlParams={{
                                search_params: getFilterParams(),
                            }}
                            defaultOrderBy={["-most_recent_ticket_signup_date"]}
                            disableCellFocus
                            onFiltersClick={() => setFiltersDrawerOpen(!open)}
                            FiltersAppliedList={FiltersAppliedList}
                            reload={reloadTable}
                            setReload={setReloadTable}
                            initialState={{
                                pinnedColumns: {
                                    right: ["actions"],
                                },
                            }}
                            getRowClassName={(params) =>
                                params.row.is_processed
                                    ? ""
                                    : params.row.ticket_count > 1
                                      ? "super-app-theme--NEW_EXISTING_SIGNUP"
                                      : "super-app-theme--NEW_SIGNUP"
                            }
                        />
                    </Box>
                </ContentBox>
            </Grid>
            <Drawer
                anchor='right'
                open={open}
                variant={isPersistentFiltersDrawer ? "persistent" : "temporary"}
                sx={{
                    width: `${sidebarWidth}rem`,
                }}
            >
                <Filters />
            </Drawer>
            <Dialog {...getEditStudentDialogProps()} maxWidth='md'>
                <DialogTitle />
                <DialogContent>
                    <Grid
                        container
                        spacing={4}
                        flexWrap={"wrap"}
                        justifyContent={"center"}
                    >
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                            }}
                        >
                            <Typography variant='button' fontWeight={"bold"}>
                                Edit Profile
                            </Typography>
                            <StudentProfileForm
                                studentProfile={editingStudent.student}
                                onSubmit={handleStudentProfileFormSubmit}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                            }}
                        >
                            <Typography
                                variant='button'
                                fontWeight={"bold"}
                                sx={{ mb: ".5rem" }}
                            >
                                Information
                            </Typography>
                            <StudentEnrollmentInformation
                                studentProfile={editingStudent.student}
                                onSubmit={handleStudentProfileFormSubmit}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
            <Dialog
                {...getStudentToProgramDialogProps()}
                maxWidth='sm'
                fullWidth
            >
                <DialogTitle>{studentsToProgramDialogTitle}</DialogTitle>
                <DialogContent>
                    <StudentsToProgramForm
                        students={selectedStudents}
                        action={studentsToProgramAction}
                        onActionComplete={handleStudentToProgramActionComplete}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                {...getDisclosureProps({
                    maxWidth: "lg",
                })}
                fullScreen
                TransitionComponent={Transition}
            >
                <DialogTitle>
                    <AppBar
                        position='fixed'
                        sx={{ backgroundColor: theme.palette.background.paper }}
                        elevation={1}
                    >
                        <Toolbar>
                            <Stack
                                direction={"row"}
                                justifyContent={"space-between"}
                                flexGrow={1}
                            >
                                <Typography color='primary' variant='h5'>
                                    Create Student Weeks
                                </Typography>{" "}
                                <Button endIcon={<Close />} onClick={onClose}>
                                    Close
                                </Button>
                            </Stack>{" "}
                        </Toolbar>
                    </AppBar>
                </DialogTitle>
                <DialogContent
                    sx={{
                        backgroundColor: theme.palette.background.default,
                    }}
                >
                    <Box sx={{ marginTop: "3rem" }}>
                        <GenerateDataForm
                            students={selectedStudents}
                            onDataGenerated={(closeModal) => {
                                showSuccessSnackbar("Data generated!");
                                if (closeModal) {
                                    onClose();
                                }
                                setReloadTable(true);
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
            <StudentImportDialog
                {...getImportDialogProps()}
                onClose={onCloseImportDialog}
                onSuccess={() => setReloadTable(true)}
            />
        </>
    );
}
